import { GetAirportsContacts, GetAirportsDetails } from '@/apis/airports';
import { ChevronRightIcon, PlusIcon, WarningIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { CustomTitle } from '@/components';
import Breadcrumbs from '@/components/Breadcrumbs';
import CustomTabs from '@/components/CustomTabs';
import { useSpin } from '@/components/SpinContext';
import TimesTable from '@/components/TimesTable';
import useUnauthorize from '@/hooks/useUnauthorize';
import {
  Alert,
  Col,
  Collapse,
  Empty,
  Flex,
  List,
  message,
  Row,
  Segmented,
  Table,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import AirFranceTabs from './AirFranceTabs';
import AirportSidebar from './AirportSidebar';
import LufthansaTabs from './LuthansaKLMTabs';
import SWminimumTime from './SWminimumTime';
import UATabsContent from './UATabsContent';
import { WarehouseTab } from './WarehouseTab';

const DAY_MAP = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};

const SWformatBusinessHours = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((item) => {
    const { is24HourFacility, day_code, open_time, close_time } = item || {};

    const hasValidTimes = open_time && close_time;
    const is24Hours = is24HourFacility === 'true';

    return {
      key: day_code || 'unknown',
      day: DAY_MAP[day_code] || day_code || 'Unknown Day',
      hours: is24Hours
        ? '24 hours'
        : hasValidTimes
          ? formatTimeRange(open_time, close_time)
          : 'Schedule Not Available',
    };
  });
};

// Helper function to format time from 'HHMM' to 'HH:MM AM/PM'
const formatTimeRange = (start, end) => {
  const formatTime = (time) => {
    let hour = parseInt(time.substring(0, 2), 10);
    let minute = time.substring(2);
    let ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  return `${formatTime(start)} – ${formatTime(end)}`;
};

const DeltaAirline = () => {
  const { showSpin, hideSpin } = useSpin();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { airlineName } = useParams();
  const location = useLocation();
  const airport = location.state?.airport;
  const airline = location.state?.airline;
  const onChange = (key) => {
    console.log(key);
  };

  const [contactsCount, setContactsCount] = useState([]);

  // Fetch all contacts
  const GetAllContacts = useCallback(() => {
    showSpin();
    const model = {
      airport: airport?.iata_code,
      airline: airline?.name,
      page: 1,
      search: '',
    };
    GetAirportsContacts(model, token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            // console.log('all conteact form server : ' , data?.data?.data)
            if (data?.data?.data && data?.data?.data?.length > 0) {
              setContactsCount(data?.data?.total_count);
            }
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [
    airline?.name,
    airport?.iata_code,
    hideSpin,
    showSpin,
    token,
    unauthorize,
  ]);

  useEffect(() => {
    GetAllContacts();
  }, [GetAllContacts]);

  const [data, setData] = useState({
    operationHours: [],
    products: [], // for lufthansa products tab
    LufthansaAddress: [], // for lufthansa addresses array
    contacts: [], // for airfrance and lufthansa contacts array
    facilities: {}, // for SW(min time),airfrance (facilities) (col & data) obj
    warehouses: [], // for airfrance warehouses array
    operatingHours: {}, // for airfrance boht customs & warehouse operation hourse
    latLng: {},
    address: {},
    facilityInfo: [],
    dropOffTime: [],
    firmsCode: '',
    portCode: '',
    IRS_Number: '',
    businessHours: [],
    pickUp: [],
    phone_number: '',
    AAcargoDropoff: [],
    AAcargoPickup: [],
  });

  const [UAdata, setUAData] = useState({
    operationHours: [],
    latLng: { lat: null, lng: null },
    address: {},
    facilityInfo: [],
    product_information: [],
    contactInfo: [],
  });

  // Fetch data from the API on page load
  // Delta airline type 2
  const fetchDeltaAirlineData = useCallback(() => {
    showSpin();
    const query = `?iata=${airport?.iata_code}&airline_type=2`;
    GetAirportsDetails(query, token)
      .then((response) => {
        hideSpin();
        try {
          const JSONresult = response?.data?.data?.data || {};

          const lat = JSONresult?.airport_latitude;
          const lng = JSONresult?.airport_longitude;

          setData({
            latLng: { lat, lng },
            operationHours: JSONresult?.operating_hours || [],
            address: {
              locationType: JSONresult?.warehouses?.[0]?.name ?? null,
              locationAddress:
                [
                  JSONresult?.airline?.facility_name ?? null,
                  JSONresult?.location?.street_address_1 ?? null,
                  JSONresult?.location?.city
                    ? JSONresult.location.city + ','
                    : null,
                  JSONresult?.location?.state ?? null,
                  JSONresult?.location?.zip_code ?? null,
                ]
                  .filter((v) => v !== null)
                  .join(' ') || null,
            },

            firmsCode: JSONresult?.customs?.firms_code || 'N/A',
            portCode: JSONresult?.customs?.port_code || 'N/A',
            facilityInfo: Array.isArray(JSONresult?.facilities)
              ? JSONresult?.facilities.map((item) => ({
                  attributes: item?.attributes || 'Unknown',
                  indicator: item?.indicator || 'Yes',
                }))
              : [],
            IRS_Number: JSONresult?.customs?.aa_irs_number || 'N/A',
            phone_number: JSONresult?.customs?.phone_number || 'N/A',
            dropOffTime: JSONresult?.timing_rules || [],
          });
        } catch (err) {
          console.error('Error processing Delta data:', err);
          message.error('Failed to process Delta Airlines data');
        }
      })
      .catch((err) => {
        hideSpin();
        console.error('Delta API error:', err);
        if (err.response) {
          message.error(
            err.response.data?.message || 'Failed to fetch Delta Airlines data'
          );
        } else {
          message.error(err.message || 'Failed to fetch Delta Airlines data');
        }
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  // american airline type 1
  const fetchAmericanAirlineData = useCallback(() => {
    showSpin();
    // let query = `${airport?.iata_code}/airlines/?type=${1}`;
    // GetAirportsData(query, token)
    const query = `?iata=${airport?.iata_code}&airline_type=1`;
    GetAirportsDetails(query, token)
      .then((results) => {
        const result = results?.data?.data || {};
        hideSpin();
        // Extract coordinates
        const lat = result?.data?.airport_latitude;
        const lng = result?.data?.airport_longitude;

        setData({
          warehouses:
            Array.isArray(result?.data?.warehouses) &&
            result.data.warehouses.length > 0
              ? result.data.warehouses
              : [],
          operationHours: {}, // placeholders

          latLng: { lat, lng },
          address: {
            locationType: 'Facility address',
            locationAddress:
              [
                result?.data?.airline?.facility_name ?? null,
                result?.data?.location?.street_address_1 ?? null,
                result?.data?.location?.city
                  ? result?.data.location.city + ','
                  : null,
                result?.data?.location?.state ?? null,
                result?.data?.location?.zip_code ?? null,
                result?.data?.location?.country ?? null,
              ]
                .filter((v) => v !== null) // remove nulls
                .join(' ') || null, // fallback to null if everything missing
          },

          firmsCode: result?.data?.customs?.firms_code || 'N/A',
          portCode: result?.data?.customs?.us_port_code || 'N/A',
          phone_number: result?.data?.customs?.phone || 'N/A',
          IRS_Number: result?.data?.customs?.irs_number || 'N/A',
          pps_policies: result?.data?.pps_policies || {},
          businessHours: result?.data?.facilityCustomsDto?.businessHours || [],

          facilityInfo: Array.isArray(result?.data?.facilities)
            ? result.data.facilities.map((item) => ({
                attributes: item?.name || 'Unknown',
                indicator: item?.is_available ? 'Yes' : 'No',
              }))
            : [],

          // transformation for AA minium time data to fit the TimesTable component
          dropOffTime: Array.isArray(result?.data?.timing_rules?.dropoff)
            ? result.data.timing_rules.dropoff.reduce((acc, item) => {
                const service = item?.service || 'Unknown';

                acc[service] = {
                  domPrescreenedTime: item?.domestic_screened || 'N/A',
                  domMinDropOffTime: item?.domestic_unscreened || 'N/A',
                  intPrescreenedTime: item?.international_screened || 'N/A',
                  intMinDropOffTime: item?.international_unscreened || 'N/A',
                };

                return acc;
              }, {})
            : {},

          pickUp: (() => {
            const dataArray = Array.isArray(result?.data?.timing_rules?.pickup)
              ? result.data.timing_rules.pickup
              : [];

            return dataArray.map((item) => ({
              serviceType: item?.service || 'Unknown',
              screenedTime: item?.domestic_screened || 'N/A', // US Domestic
              unScreenedTime: item?.international_screened || 'N/A', // International
            }));
          })(),

          AAcargoDropoff: result?.data?.cargoDropOff || [],
          AAcargoPickup: result?.data?.ppsPickup || [],
        });
      })
      .catch((err) => {
        hideSpin();
        message.error(err);
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  // SouthWest airline type 3
  const fetchSouthWestAirlineData = useCallback(() => {
    showSpin();
    const query = `?iata=${airport?.iata_code}&airline_type=3`;
    GetAirportsDetails(query, token)
      .then((resp) => {
        const result = resp?.data?.data?.data || {};
        // Extract coordinates
        const lat = result?.airport_latitude;
        const lng = result?.airport_longitude;
        setData({
          operationHours:
            Array.isArray(result?.operating_hours) &&
            result?.operating_hours?.length > 0
              ? result?.operating_hours
              : [],
          latLng: { lat, lng },

          address: {
            locationType: result?.airline?.code ?? null,
            locationAddress:
              [
                result?.airline?.facility_name ?? null,
                result?.location?.street_address_1 ?? null,
                result?.location?.city ? result.location.city + ',' : null,
                result?.location?.state ?? null,
                result?.location?.zip_code ?? null,
              ]
                .filter((v) => v !== null) // remove nulls
                .join(' ') || null, // fallback to null if everything missing
          },
          firmsCode: result?.customs?.firms_code || 'N/A',
          portCode: result?.customs?.port_code || 'N/A',
          // servicing / facilites for sidebar bottom
          facilityInfo: Array.isArray(result?.facilities)
            ? result?.facilities.map((item) => ({
                attributes: item || 'Unknown',
                indicator: 'Yes',
              }))
            : [],

          facilities: result?.minimum_times || {}, // Southwest minimum times
          IRS_Number: result?.customs?.aa_irs_number || 'N/A',
          phone_number: result?.customs?.phone_number || 'N/A',
        });
      })
      .catch((err) => {
        message.error(err);
      })
      .finally(() => {
        hideSpin();
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  // United airline type 4
  const fetchUnitedAirlineData = useCallback(() => {
    showSpin();
    const query = `?iata=${airport?.iata_code}&airline_type=${4}`;
    GetAirportsDetails(query, token)
      .then((resp) => {
        const result = resp?.data?.data?.data || {};
        // Extract coordinates
        const lat = result?.airport_latitude;
        const lng = result?.airport_longitude;
        setUAData({
          product_information: result?.response_data?.product_information || [],
          contactInfo: result?.contact_info || [],
        });
        setData({
          latLng: { lat, lng },
          firmsCode: result?.customs?.firms_code || 'N/A',
          portCode: result?.customs?.port_code || 'N/A',
          IRS_Number: result?.customs_irs_number || 'N/A',
          phone_number: result?.customs?.phone_number || 'N/A',
          // servicing / facilites for sidebar bottom
          facilityInfo: Array.isArray(result?.facilities)
            ? result?.facilities.map((item) => ({
                attributes: item || 'Unknown',
                indicator: 'Yes',
              }))
            : [],

          operationHours:
            Array.isArray(result?.operating_hours) &&
            result?.operating_hours?.length > 0
              ? result?.operating_hours
              : [],

          address: {
            locationType: result?.airline?.code ?? null,
            locationAddress:
              [
                result?.location?.address ?? null,
                result?.location?.street_address_1 ?? null,
                result?.location?.city ? result.location.city + ',' : null,
                result?.location?.state ?? null,
                result?.location?.zip_code ?? null,
              ]
                .filter((v) => v !== null) // remove nulls
                .join(' ') || null, // fallback to null if everything missing
          },
        });
      })
      .catch((err) => {
        message.error(err?.massage);
      })
      .finally(() => {
        hideSpin();
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  // Lufthansa type 5
  const fetchLufthansaData = useCallback(() => {
    showSpin();
    const query = `?iata=${airport?.iata_code}&airline_type=5`;

    GetAirportsDetails(query, token)
      .then((response) => {
        if (!response || !response.data) {
          throw new Error('No data returned from API.');
        }

        const result = response.data.data;
        if (!result) {
          throw new Error('Invalid API response structure.');
        }

        const lat = result?.data?.airport_latitude;
        const lng = result?.data?.airport_longitude;

        setData({
          operationHours: result?.data?.operating_hours || {},
          products: result?.data?.products || [],
          latLng: { lat, lng },

          LufthansaAddress: result?.data?.addresses || [],
          contacts: result?.data?.contacts || [], // array for both lufthansa and airfrance contacts

          firmsCode: result?.data?.firms_code || 'N/A',

          facilityInfo: Array.isArray(result?.data?.servicing)
            ? result.data.servicing.map((item) => ({
                attributes: item?.name || 'Unknown',
                indicator: item?.is_available ? 'Yes' : 'No',
              }))
            : [],
        });
        hideSpin();
      })
      .catch((err) => {
        hideSpin();
        message.error(err?.message);
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  // Airfrance KLM type 6
  const fetchAirfranceKLMData = useCallback(() => {
    showSpin();
    let query = `?iata=${airport?.iata_code}&airline_type=${6}`;
    GetAirportsDetails(query, token)
      .then((results) => {
        const result = results?.data?.data || {};
        hideSpin();
        // Extract coordinates
        const lat = result?.data?.airport_latitude;
        const lng = result?.data?.airport_longitude;

        setData({
          products: result?.data?.products || [],
          facilities: result?.data?.facilities || {},
          warehouses: result?.data?.warehouses || [],
          contacts: result?.data?.contacts || [], // array for both lufthansa and airfrance contacts
          // array for both lufthansa and airfrance contacts
          operatingHours:
            result?.data?.operating_hours &&
            typeof result.data.operating_hours === 'object'
              ? result.data.operating_hours
              : {},
          operationHours: 'N/A',
          latLng: { lat, lng },
          firmsCode: result?.data?.facilityCustomsDto?.firmsCode || 'N/A',
          portCode: result?.data?.facilityCustomsDto?.usPortCode || 'N/A',
          facilityInfo: Array.isArray(result?.data?.servicing)
            ? result.data.servicing.map((item) => ({
                attributes: item?.name || 'Unknown',
                indicator: item?.is_available ? 'Yes' : 'No',
              }))
            : [],
        });
      })
      .catch((err) => {
        hideSpin();
        message.error(err?.message);
      });
  }, [airport?.iata_code, hideSpin, showSpin, token]);

  useEffect(() => {
    if (airline?.name === 'Delta Airlines') {
      fetchDeltaAirlineData();
    }

    if (airline?.name === 'American Airlines') {
      fetchAmericanAirlineData();
    }

    if (airline?.name === 'Southwest Airlines') {
      fetchSouthWestAirlineData();
    }
    if (airline?.name === 'United Airlines') {
      fetchUnitedAirlineData();
    }
    if (airline?.name === 'Lufthansa') {
      fetchLufthansaData();
    }
    if (airline?.name === 'Airfrance KLM') {
      fetchAirfranceKLMData();
    }
  }, [
    airline?.name,
    fetchAmericanAirlineData,
    fetchDeltaAirlineData,
    fetchSouthWestAirlineData,
    fetchUnitedAirlineData,
    fetchLufthansaData,
    fetchAirfranceKLMData,
  ]);

  const renderHours = (categoryData) => {
    return (
      <List
        className='zl-opening-hours-list zl-custom-collapse-data'
        dataSource={Object.entries(categoryData).map(([day, times]) => ({
          day,
          // Ensure times is always an array before calling join()
          time: Array.isArray(times) ? times.join(', ') : times || 'N/A', // If it's not an array, fallback to times as is or "N/A"
          closed: false, // Assuming no closed status in your data, update accordingly if required
        }))}
        renderItem={(item) => (
          <List.Item>
            <Flex align='center' style={{ width: '100%' }}>
              <span className='list-item'>{item.day}</span>
              <div className='zl-badge-item'>
                <time className='list-item'>{item.time}</time>
              </div>
            </Flex>
          </List.Item>
        )}
      />
    );
  };

  // Dynamically render collaps data
  const DeltaAirlinesitems = Object.entries(data?.operationHours).map(
    ([category, categoryData], index) => ({
      key: String(index),
      label: category, // Using category name as the label
      children: renderHours(categoryData), // Using the transformed category data
    })
  );

  // const [_summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  // const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 991);

  // useEffect(() => {
  //   const handleResize = () => {
  //     setIsDesktop(window.innerWidth >= 991);
  //   };

  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  const breadcrumbItems = [
    {
      title: 'Airports',
      href: '/airport-list',
    },
    {
      title: airport?.name,
      href: `/airport-list/airport/${airport?.iata_code}`,
      state: { airport },
    },
    {
      title: airline?.name,
    },
  ];

  const containerStyle = {
    width: '100%',
    height: '208px',
    borderRadius: '12px 12px 0 0', // Top border-radius only
    overflow: 'hidden',
  };

  const lat = data?.latLng?.lat;
  const lng = data?.latLng?.lng;

  const hasValidCoords = Number.isFinite(lat) && Number.isFinite(lng);

  const center = hasValidCoords ? { lat, lng } : null;

  // american airline table data

  // Helper function to convert abbreviated day to full day name
  const getFullDayName = (abbreviation) => {
    const dayNames = {
      MON: 'Monday',
      TUE: 'Tuesday',
      WED: 'Wednesday',
      THU: 'Thursday',
      FRI: 'Friday',
      SAT: 'Saturday',
      SUN: 'Sunday',
    };
    return dayNames[abbreviation] || abbreviation;
  };
  const columns = [
    {
      title: '',
      dataIndex: 'day',
      key: 'day',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: '',
      dataIndex: 'hours',
      key: 'hours',
      align: 'right',
    },
  ];

  // Function to generate table data from businessHours for American airline
  const generateTableData = (businessHours) => {
    const result = [];

    businessHours?.forEach((hourBlock, blockIndex) => {
      hourBlock.days.forEach((day, dayIndex) => {
        result.push({
          key: `${blockIndex}-${dayIndex}`,
          day: getFullDayName(day),
          hours: hourBlock.times[0] || 'N/A',
        });
      });
    });

    return result;
  };
  const dataSource = generateTableData(data?.businessHours);

  // Southwest airline operating hours
  const SWcolumns = [
    {
      title: 'Day',
      dataIndex: 'day',
      key: 'day',
      align: 'left',
    },
    {
      title: 'Hours',
      dataIndex: 'hours',
      key: 'hours',
      align: 'right',
    },
  ];
  const SWdataSource = SWformatBusinessHours(data?.operationHours); // american airline

  const onChanges = () => {};

  // Transformer for new backend format → TabsContent format
  const itemss = data?.warehouses?.map((facility, index) => ({
    key: `${index}`,
    label: facility.name,
    children: <WarehouseTab facility={facility} />,
  }));

  const [selectedTab, setSelectedTab] = useState('dropoff');

  const [_currentTabsData, setCurrentTabsData] = useState({});

  const UATabsItemss = useMemo(
    () =>
      Object.entries(UAdata?.product_information || {})
        .map(([_, tab], index) => {
          if (!tab) return null; // Safety check
          return {
            key: String(index),
            label: `${tab?.name || ''}`,
            tabData: tab, // Save original data
            children: <UATabsContent data={tab || {}} />,
          };
        })
        .filter(Boolean),
    [UAdata?.product_information]
  );
  useEffect(() => {
    if (UATabsItemss.length > 0) {
      setCurrentTabsData(UATabsItemss[0]);
    }
  }, [UATabsItemss]);

  const onUAChanges = (key) => {
    const currentTab = UATabsItemss.find((item) => item.key === key);
    setCurrentTabsData(currentTab);
  };

  // Function to determine the external link based on airline name
  const getAirlineExternalLink = () => {
    const iataCode = airport?.iata_code || '';
    switch (airline?.name) {
      case 'Delta Airlines':
        return `https://www.deltacargo.com/Cargo/catalog/worldwide-locations?AirportName=${iataCode}`;
      case 'American Airlines':
        return `https://www.aacargo.com/AACargo/ua/facilities/${iataCode}`;
      case 'Southwest Airlines':
        return `https://www.swacargo.com/swacargo_com_ui/ship/facilities/facility?airportCode=${iataCode}`;
      case 'United Airlines':
        return `https://www.unitedcargo.com/en/us/learn/station-information/${iataCode}`;
      case 'Lufthansa':
        return `https://www.lufthansa-cargo.com/en/network/maps/stations/details/${iataCode}`;
      case 'Airfrance KLM':
        return `https://www.afklcargo.com/mycargo/network/warehouse/${iataCode}`;
      default:
        return null;
    }
  };

  const externalLink = getAirlineExternalLink();

  const hideSomeSection = ![
    'American Airlines',
    'Lufthansa',
    'Airfrance KLM',
    'United Airlines',
  ].includes(airline?.name ?? '');
  return (
    <section className='airline-detail'>
      <Row gutter={[24, 36]}>
        {/* left side service hours and time  */}
        <Col xxl={16} xl={16} lg={16} md={24} sm={24} xs={24}>
          <Flex vertical gap={16}>
            <Breadcrumbs items={breadcrumbItems} />
            <Flex vertical gap={hideSomeSection && 24}>
              <Flex vertical gap={4}>
                <CustomTitle
                  className='zl-custom-title'
                  text={`${airport?.name}${airport?.iata_code?.trim() ? ` (${airport?.iata_code})` : ''}`}
                  fontSize='24px'
                  fontWeight='700'
                  lineHeight='32px'
                  color='var(--text-primary)'
                  marginBottom='0'
                />
                <CustomTitle
                  text={airline?.name}
                  fontSize='18px'
                  fontWeight='700'
                  lineHeight='24px'
                  color='var(--text-secondary)'
                  marginBottom='0'
                  marginTop='0'
                />
                {/* {!isDesktop && (
                  <button
                    className='zl-custom-btn'
                    onClick={() => setSummaryDrawerOpen(true)}
                  >
                    Summary
                  </button>
                )} */}
              </Flex>
              <Flex vertical gap={12}>
                {hideSomeSection && (
                  <CustomTitle
                    text='Operating hours'
                    fontSize='16px'
                    fontWeight='600'
                    lineHeight='20px'
                    color='var(--text-primary)'
                    marginBottom='0'
                    marginTop='0'
                  />
                )}
                {/* Lufthansa */}
                {airline?.name === 'Lufthansa' && (
                  <div className='zl-custom-tabs' style={{ marginTop: '10px' }}>
                    <LufthansaTabs
                      operationHours={data?.operationHours}
                      products={data?.products || []}
                    />
                  </div>
                )}
                {airline?.name == 'Airfrance KLM' && (
                  <div className='zl-custom-tabs' style={{ marginTop: '10px' }}>
                    <AirFranceTabs
                      operationHours={data?.operatingHours}
                      products={data?.products}
                      facilities={data?.facilities}
                    />
                  </div>
                )}

                {airline?.name === 'United Airlines' && (
                  <>
                    {UATabsItemss?.length > 0 ? (
                      <div className='zl-custom-tabs'>
                        <CustomTabs
                          defaultActiveKey='0'
                          items={UATabsItemss}
                          onChange={onUAChanges}
                        />
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </>
                )}

                {airline?.name === 'American Airlines' && (
                  <div className='zl-custom-tabs' style={{ marginTop: '10px' }}>
                    <CustomTabs items={itemss} onChange={onChanges} />
                  </div>
                )}
                {airline?.name === 'Delta Airlines' ? (
                  <>
                    {DeltaAirlinesitems?.length > 0 ? (
                      <Collapse
                        className='zl-custom-collapse'
                        items={DeltaAirlinesitems}
                        onChange={onChange}
                      />
                    ) : (
                      <p
                        style={{
                          color: 'var(--text-secondary)',
                          fontSize: '12px',
                          textAlign: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        {' '}
                        Operating hours not available{' '}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {airline?.name === 'Southwest Airlines' ? (
                      <Table
                        className='zl-custom-times-table'
                        columns={SWcolumns}
                        dataSource={SWdataSource}
                        pagination={false}
                        rowHoverable={false}
                        showHeader={false}
                        bordered={true}
                        size='middle'
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      <>
                        {hideSomeSection &&
                        airline?.name !== 'United Airlines' ? (
                          <Table
                            className='zl-custom-times-table'
                            columns={columns}
                            dataSource={dataSource}
                            pagination={false}
                            rowHoverable={false}
                            showHeader={false}
                            bordered={true}
                            size='middle'
                            style={{ borderRadius: '8px' }}
                          />
                        ) : null}
                      </>
                    )}
                  </>
                )}
              </Flex>

              {airline?.name === 'Southwest Airlines' ? (
                <SWminimumTime data={data?.facilities} />
              ) : (
                <>
                  {airline?.name === 'American Airlines' ? (
                    <>
                      <Flex
                        align='center'
                        justify='space-between'
                        wrap
                        style={{ marginBottom: '10px', marginTop: '20px' }}
                      >
                        <CustomTitle
                          text='Minimum times'
                          fontSize='16px'
                          fontWeight='600'
                          lineHeight='20px'
                          color='var(--text-primary)'
                          marginTop='15px'
                        />
                        <Segmented
                          className='zl-custom-segmented'
                          value={selectedTab}
                          onChange={setSelectedTab}
                          options={[
                            { label: 'Dropoff', value: 'dropoff' },
                            { label: 'Pickup', value: 'pickup' },
                          ]}
                        />
                      </Flex>
                      <TimesTable
                        data={
                          selectedTab === 'dropoff'
                            ? data?.dropOffTime
                            : data?.pickUp
                        }
                        selectedTab={selectedTab}
                      />
                    </>
                  ) : (
                    <>
                      {![
                        'United Airlines',
                        'Lufthansa',
                        'Airfrance KLM',
                      ].includes(airline?.name ?? '') && (
                        <Flex vertical gap={12}>
                          <CustomTitle
                            text='Minimum dropoff times'
                            fontSize='16px'
                            fontWeight='600'
                            lineHeight='20px'
                            color='var(--text-primary)'
                            marginBottom='0'
                            marginTop='0'
                          />
                          <TimesTable
                            data={data?.dropOffTime}
                            selectedTab='dropoff'
                          />
                        </Flex>
                      )}
                    </>
                  )}
                </>
              )}
            </Flex>
            {airline?.name === 'American Airlines' &&
            Object.entries(data?.pps_policies || {}).length > 0 ? (
              <Flex gap={12} vertical style={{ marginTop: '10px' }}>
                <CustomTitle
                  text='PPS Policies'
                  fontSize='16px'
                  fontWeight='600'
                  lineHeight='20px'
                  color='var(--text-primary)'
                  marginBottom='0'
                  marginTop='0'
                />
                <Alert
                  className='zl-custom-warning-message'
                  style={{ width: 'fit-content' }}
                  message={
                    selectedTab === 'dropoff'
                      ? data?.pps_policies?.dropoff || 'N/A'
                      : data?.pps_policies?.pickup || 'N/A'
                  }
                  type='warning'
                  showIcon
                  icon={
                    <WarningIcon
                      fill='var(--text-primary)'
                      width={24}
                      height={24}
                    />
                  }
                />
              </Flex>
            ) : null}
          </Flex>
        </Col>

        {/* right side map and address  */}

        <Col xxl={8} xl={8} lg={8} md={24} sm={24} xs={24}>
          <Flex vertical gap={16}>
            <Link
              to={`/airport-list/${airlineName}/company-contact`}
              state={{ airport, airline }}
              className='zl-company-contact'
              style={{ gap: '16px', justifyContent: 'flex-start' }}
            >
              {contactsCount < 1 && (
                <PlusIcon
                  fill='var(--text-secondary)'
                  width={24}
                  height={24}
                  className=''
                />
              )}
              <Flex style={{ justifyContent: 'space-between', width: '100%' }}>
                <CustomTitle
                  text={
                    contactsCount > 0
                      ? `${contactsCount} company contacts`
                      : 'Add company contacts'
                  }
                  fontSize='16px'
                  fontWeight='600'
                  lineHeight='24px'
                  color='var(--text-primary)'
                  marginBottom='0'
                  marginTop='0'
                />

                {contactsCount > 0 && (
                  <ChevronRightIcon
                    fill='var(--text-secondary)'
                    width={24}
                    height={24}
                    className=''
                  />
                )}
              </Flex>
            </Link>

            <div>
              <AirportSidebar
                airline={airline} // e.g., { name: 'Lufthansa' }
                data={data} // main airport data object
                UAdata={UAdata} // United Airlines specific data
                center={center} // Google Map center coordinates
                containerStyle={containerStyle} // Map container CSS
              />
            </div>
          </Flex>
          <p className='zl-page-content'>
            The data on this page is sourced from third-party websites and may
            be outdated or inaccurate. Always verify details with the{' '}
            {externalLink ? (
              <a href={externalLink} target='_blank' rel='noopener noreferrer'>
                official website
              </a>
            ) : (
              'official website'
            )}
            .
          </p>
        </Col>
      </Row>
    </section>
  );
};

export default DeltaAirline;
